import { Action, Dispatch } from 'redux';
import { fetchOrganisationsSuccess } from './organisationReducer';
import {
  ApiOrganisationCurrencyEnum,
  ApiOrganisationDataType,
  organisationApi,
  organisationCloneInternalApi,
} from '../apis';
import { showFeedback } from '../feedback/feedbackReducer';
import { OrganisationModalValues } from './OrganisationModal';
import { downloadDocument } from '../common/document/helper';

export const fetchOrganisations =
  (page?: number, size?: number, includeInactive?: boolean, query?: string) =>
  async (dispatch: Dispatch): Promise<Action> => {
    const organisations = await organisationApi.findAllOrganisations({
      page,
      size,
      includeInactive,
      query,
    });
    return dispatch(fetchOrganisationsSuccess(organisations));
  };

export const saveOrganisation =
  ({ id, name, demo, currency }: OrganisationModalValues & { demo: boolean }) =>
  async (dispatch: Dispatch) => {
    const apiOrganisation = {
      id: id,
      name: name,
      currency: ApiOrganisationCurrencyEnum[currency.value as keyof typeof ApiOrganisationCurrencyEnum],
    };
    if (id !== undefined) {
      await organisationApi.updateOrganisation({
        apiOrganisation: apiOrganisation,
      });
      dispatch(showFeedback({ message: `Organisation #${id} updated!`, success: true }));
    } else {
      const organisation = await organisationApi.addOrganisation({
        apiOrganisation: apiOrganisation,
        demo,
      });
      dispatch(
        showFeedback({
          message: `Organisation #${organisation.id} added!`,
          success: true,
        })
      );
    }
  };

export const deactivateOrganisation = async (organisationId: number) => {
  await organisationApi.deactivateOrganisation({ organisationId });
};

export const deleteOrganisationData =
  (dataType: ApiOrganisationDataType, organisationId: number) => async (dispatch: Dispatch) => {
    try {
      await organisationApi.deleteOrganisationData({ dataType, organisationId });
      dispatch(
        showFeedback({
          message: 'Deleted',
          success: true,
        })
      );
    } catch (_) {
      dispatch(
        showFeedback({
          message: 'Oops, something went wrong!',
          success: false,
        })
      );
    }
  };

export const activateOrganisation = async (organisationId: number) => {
  await organisationApi.activateOrganisation({ organisationId });
};

export const importOrganisationConfig =
  (file: File, name: string, currency: ApiOrganisationCurrencyEnum) =>
  async (dispatch: Dispatch): Promise<{ organisationId: number }> => {
    const result = await organisationCloneInternalApi.importOrganisationConfig({ file, name, currency });
    dispatch(
      showFeedback({
        message: `Organisation '${name}' imported!`,
        success: true,
      })
    );
    return { organisationId: result.organisationId };
  };

export const exportOrganisationConfig =
  (organisationId: number) =>
  async (_dispatch: Dispatch): Promise<void> => {
    const result = await organisationCloneInternalApi.exportOrganisationConfig({ organisationId });
    const raw = result.headers.get('Content-Disposition')?.split('filename=')[1];
    const filename = raw ? raw.replace(/^"|"$/g, '') : `org-config-${organisationId}.json`;
    downloadDocument(result.body, filename);
  };
